/**
 * ActiveJobsBanner Component
 *
 * Compact banner showing active story generation jobs.
 * Collapsible with job progress indicators.
 */

import type { ReactElement } from 'react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, ChevronUp, Loader2, Sparkles } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export interface ActiveJob {
  id: string;
  status: 'queued' | 'processing' | 'completed' | 'failed';
  progress?: number;
  queue_position?: number;
  title?: string;
  prompt?: string;
  thumbnail_url?: string;
  created_at: string;
}

interface ActiveJobsBannerProps {
  jobs: ActiveJob[];
}

// Helper to get full image URL
const getImageUrl = (url: string | undefined | null): string | null => {
  if (!url) return null;
  if (url.startsWith('/static/')) {
    return `${BACKEND_URL}${url}`;
  }
  return url;
};

const ActiveJobsBanner = ({ jobs }: ActiveJobsBannerProps): ReactElement | null => {
  const navigate = useNavigate();
  const [isExpanded, setIsExpanded] = useState(true);

  if (jobs.length === 0) return null;

  return (
    <div
      className="rounded-xl mb-4 overflow-hidden shadow-sm"
      style={{ background: 'var(--st-bg-surface)', border: '1px solid var(--st-border-light)' }}
    >
      {/* Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between p-3 hover:bg-black/5 dark:hover:bg-white/5 transition-colors"
      >
        <div className="flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" style={{ color: 'var(--st-primary)' }} />
          <span
            className="font-bold text-sm"
            style={{ fontFamily: 'Chewy, cursive', color: 'var(--st-text-heading)' }}
          >
            Creating Your {jobs.length === 1 ? 'Story' : 'Stories'}
          </span>
          <span
            className="px-2 py-0.5 rounded-full text-xs font-semibold"
            style={{ background: 'var(--st-border-light)', color: 'var(--st-primary)' }}
          >
            {jobs.length}
          </span>
        </div>
        {isExpanded ? (
          <ChevronUp className="w-4 h-4" style={{ color: 'var(--st-text-muted)' }} />
        ) : (
          <ChevronDown className="w-4 h-4" style={{ color: 'var(--st-text-muted)' }} />
        )}
      </button>

      {/* Expandable Content */}
      {isExpanded && (
        <div className="px-3 pb-3 space-y-2">
          {jobs.map((job) => (
            <div
              key={job.id}
              className="flex items-center gap-3 p-2 rounded-lg hover:bg-black/5 dark:hover:bg-white/5 cursor-pointer transition-colors"
              onClick={() => navigate(`/generating/${job.id}`)}
            >
              {/* Thumbnail with spinner overlay */}
              <div
                className="relative w-10 h-10 rounded-lg overflow-hidden flex-shrink-0"
                style={{ background: 'var(--st-border-light)' }}
              >
                {job.thumbnail_url ? (
                  <>
                    <img
                      src={getImageUrl(job.thumbnail_url) || undefined}
                      alt="Preview"
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                      <Loader2 className="w-4 h-4 text-white animate-spin" />
                    </div>
                  </>
                ) : (
                  <div
                    className="w-full h-full flex items-center justify-center relative"
                    style={{
                      background: 'linear-gradient(135deg, var(--st-primary-hover) 0%, var(--st-primary) 100%)',
                    }}
                  >
                    <Sparkles className="w-3 h-3 text-white/80" />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <Loader2 className="w-4 h-4 text-white animate-spin" />
                    </div>
                  </div>
                )}
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p
                    className="text-sm font-semibold truncate"
                    style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-heading)' }}
                  >
                    {job.title || 'Creating...'}
                  </p>
                  <span
                    className="px-1.5 py-0.5 rounded-full text-[10px] font-semibold animate-pulse flex-shrink-0"
                    style={{ background: 'var(--st-border-light)', color: 'var(--st-primary)' }}
                  >
                    {job.status === 'queued' ? `#${job.queue_position}` : `${job.progress || 0}%`}
                  </span>
                </div>

                {/* Progress bar for processing */}
                {job.status === 'processing' && (
                  <div
                    className="mt-1 h-1 rounded-full overflow-hidden"
                    style={{ background: 'var(--st-border-light)' }}
                  >
                    <div
                      className="h-full rounded-full transition-all duration-500"
                      style={{
                        width: `${job.progress || 0}%`,
                        background: 'linear-gradient(90deg, var(--st-primary-hover) 0%, var(--st-primary) 100%)',
                      }}
                    />
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ActiveJobsBanner;
